import * as React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { StepState as StepStateType } from './types';

type Props = {
  children: React.ReactNode;
};

type ConsumerProps = {
  children: (props: StepStateContextType) => React.ReactElement<StepStateContextType>;
};

export type StepStateContextType = {
  stepState: StepStateType;
  setStepState: (x0: Partial<StepStateType>) => void;
};

export const StepStateContext = createContext<StepStateContextType>({
  stepState: {
    isSubmitting: false,
    isSaving: false,
    isAutoSaving: false,
    isDeadlineExpired: false,
    showSubmitSuccessModal: false,
    showValidation: false,
    errorCode: null,
  },
  setStepState: () => undefined,
});

export const useStepStateContext = () => {
  return useContext(StepStateContext);
};

export const StepStateContextProvider = ({ children }: Props) => {
  const [stepState, setState] = useState<StepStateType>({
    isSubmitting: false,
    isSaving: false,
    isAutoSaving: false,
    isDeadlineExpired: false,
    showSubmitSuccessModal: false,
    showValidation: false,
    errorCode: null,
  });

  const setStepState = useCallback<(...args: $TSFixMe[]) => $TSFixMe>((newState: StepStateType) => {
    setState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  }, []);

  const value = useMemo(
    () => ({
      stepState,
      setStepState,
    }),
    [stepState, setStepState]
  );

  return <StepStateContext.Provider value={value}>{children}</StepStateContext.Provider>;
};

export const StepStateContextConsumer: React.FC<ConsumerProps> = ({ children }) => {
  const { stepState, setStepState } = useStepStateContext();
  return children({ stepState, setStepState });
};

export default StepStateContextConsumer;
